/* ==========================================================================
   <predictive-search> — combobox + listbox (WAI-ARIA APG pattern)
   - debounced fetch (200ms) of sections/predictive-search via Section Rendering
   - AbortController cancels stale requests, results cached per query
   - ArrowUp/Down move aria-activedescendant, Enter opens the active option,
     Esc clears the query (second Esc closes the dialog natively)
   - Without JS the form is a plain GET /search.
   ========================================================================== */
if (!customElements.get('predictive-search')) {
  const RESOURCES = 'product,collection,query,article';

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[role="combobox"]');
      if (!this.input) return;
      this.form = this.input.form;
      this.listbox = this.querySelector('[data-search-options]');
      this.footer = this.querySelector('[data-search-footer]');
      this.results = this.querySelector('[data-search-results]');
      this.discovery = this.querySelector('[data-search-discovery]');
      this.status = this.querySelector('[data-search-status]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.dialog = this.closest('dialog');
      this.cache = new Map();
      this.options = [];
      this.active = -1;

      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        this.syncClear();
        timer = setTimeout(() => this.onInput(), 200);
      });
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.clearBtn?.addEventListener('click', () => { this.clear(); this.input.focus(); });
      this.form?.addEventListener('submit', (e) => { if (!this.query) e.preventDefault(); });

      // Options are clicked through their inner link; hover syncs the active option.
      this.listbox.addEventListener('mousemove', (e) => {
        const opt = e.target.closest('[role="option"]');
        if (opt) this.setActive(this.options.indexOf(opt), false);
      });

      if (this.dialog) {
        this.dialog.addEventListener('dialog:open', () => {
          requestAnimationFrame(() => { this.input.focus(); this.input.select(); });
        });
        // First Esc with a query clears instead of closing.
        this.dialog.addEventListener('cancel', (e) => {
          if (this.input.value && document.activeElement === this.input) {
            e.preventDefault();
            this.clear();
          }
        });
      }
      this.syncClear();
      if (this.query) this.onInput();
    }

    get query() { return this.input.value.trim(); }

    syncClear() { if (this.clearBtn) this.clearBtn.hidden = !this.input.value; }

    onInput() {
      const q = this.query;
      if (!q) { this.reset(); return; }
      this.load(q);
    }

    async load(q) {
      this.controller?.abort();
      if (this.cache.has(q)) { this.render(this.cache.get(q)); return; }
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');
      const u = new URL(this.dataset.url, location.origin);
      u.searchParams.set('q', q);
      u.searchParams.set('resources[type]', RESOURCES);
      u.searchParams.set('resources[limit]', '6');
      u.searchParams.set('resources[limit_scope]', 'each');
      u.searchParams.set('section_id', this.dataset.sectionId || 'predictive-search');
      try {
        const res = await fetch(u, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        this.cache.set(q, html);
        if (q === this.query) this.render(html);
      } catch (err) {
        if (err.name !== 'AbortError') this.reset();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const opts = doc.querySelector('[data-predictive-options]');
      const foot = doc.querySelector('[data-predictive-footer]');
      const status = doc.querySelector('[data-predictive-status]');
      this.listbox.innerHTML = opts ? opts.innerHTML : '';
      this.footer.innerHTML = foot ? foot.innerHTML : '';
      this.options = Array.from(this.listbox.querySelectorAll('[role="option"]'));
      this.options.forEach((o, i) => { o.id = `PredictiveOption-${i}`; });
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.discovery.hidden = true;
      this.results.hidden = false;
      this.listbox.hidden = this.options.length === 0;
      this.input.setAttribute('aria-expanded', String(this.options.length > 0));
      if (this.status) this.status.textContent = status ? status.textContent.trim() : '';
    }

    reset(showDiscovery = true) {
      this.controller?.abort();
      this.listbox.innerHTML = '';
      this.footer.innerHTML = '';
      this.options = [];
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', 'false');
      this.results.hidden = true;
      if (showDiscovery) this.discovery.hidden = false;
      if (this.status) this.status.textContent = '';
    }

    clear() {
      this.input.value = '';
      this.syncClear();
      this.reset();
    }

    setActive(i, scroll = true) {
      if (this.active > -1 && this.options[this.active]) this.options[this.active].setAttribute('aria-selected', 'false');
      this.active = i;
      const opt = this.options[i];
      if (!opt) { this.input.removeAttribute('aria-activedescendant'); return; }
      opt.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', opt.id);
      if (scroll) opt.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.input.value) { e.preventDefault(); e.stopPropagation(); this.clear(); }
        return; // empty → native dialog Esc closes
      }
      if (!this.options.length || this.results.hidden) return;
      const n = this.options.length;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.setActive(this.active + 1 >= n ? 0 : this.active + 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.setActive(this.active - 1 < 0 ? n - 1 : this.active - 1);
          break;
        case 'Enter': {
          const link = this.options[this.active]?.querySelector('a');
          if (link) { e.preventDefault(); link.click(); }
          break;
        }
        default:
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);
}
